"use client";

import { useState } from "react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

import {
  TrendingUpIcon,
  WalletIcon,
  ClockIcon,
  AlertTriangleIcon,
} from "lucide-react";
import { useGetStatistic } from "modules/invoice/api";
import { Spinner } from "components/Spinner";
import { RevenueDistributionChart } from "modules/invoice/components/src/repartitionGraph";
import { RevenueEvolutionChart } from "modules/invoice/components/src/evolutionGraph";
import DateRangePicker from "./components/filter/dateRangePicker";
import { OnHoldInvoiceTable } from "modules/invoice/components";
import { LateInvoiceTable } from "modules/invoice/components/src/lateInvoiceTable";

const periods = [
  { key: "", label: "Tout" },
  { key: "month", label: "1 mois" },
  { key: "3months", label: "3 mois" },
  { key: "6months", label: "6 mois" },
  { key: "year", label: "1 an" },
];

const formatCurrency = (value: number) =>
  value.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export default function Dashboard() {
  const [date, setDate] = useState<DateRange | undefined>(undefined);
  const [activePeriod, setActivePeriod] = useState<string | null>("");

  const formatDateForApi = (date: Date) => format(date, "MM/dd/yyyy");

  const handlePredefinedPeriod = (period: string) => {
    setActivePeriod(period);
    const now = new Date();
    let from: Date;

    switch (period) {
      case "month":
        from = new Date(now.setMonth(now.getMonth() - 1));
        break;
      case "3months":
        from = new Date(now.setMonth(now.getMonth() - 3));
        break;
      case "6months":
        from = new Date(now.setMonth(now.getMonth() - 6));
        break;
      case "year":
        const customFrom =
          now.getFullYear() === 2025
            ? new Date(2024, 5, 30)
            : new Date(
                now.setFullYear(
                  now.getFullYear() - 1,
                  now.getMonth(),
                  now.getDate()
                )
              );
        const customTo =
          now.getFullYear() === 2025
            ? new Date(2025, 6, 1)
            : new Date(
                now.setFullYear(
                  now.getFullYear(),
                  now.getMonth(),
                  now.getDate()
                )
              );
        setDate({ from: customFrom, to: customTo });
        return;
      default:
        setDate(undefined);
        return;
    }

    if (from) {
      setDate({ from, to: new Date() });
    }
  };

  const { data: statistic, isLoading } = useGetStatistic(
    date?.from ? formatDateForApi(date.from) : undefined,
    date?.to ? formatDateForApi(date.to) : undefined
  );

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[90vh] w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const cards = [
    {
      title: "Total factures",
      value: statistic?.totalInvoiced ?? 0,
      icon: WalletIcon,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      description: "Montant total facturé",
    },
    {
      title: "Encaissé",
      value: statistic?.totalPaid ?? 0,
      icon: TrendingUpIcon,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      description: `${statistic?.percentages?.paid ?? 0}% du total`,
      change: statistic?.percentageChanges?.paid,
    },
    {
      title: "En attente",
      value: statistic?.totalOnHold ?? 0,
      icon: ClockIcon,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      description: `${statistic?.percentages?.onHold ?? 0}% du total`,
      change: statistic?.percentageChanges?.onHold,
    },
    {
      title: "En retard",
      value: statistic?.totalLate ?? 0,
      icon: AlertTriangleIcon,
      color: "text-red-600",
      bgColor: "bg-red-50",
      description: `${statistic?.percentages?.late ?? 0}% du total`,
      change: statistic?.percentageChanges?.late,
    },
  ];

  return (
    <div className="space-y-6 p-3 sm:p-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          {periods.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handlePredefinedPeriod(key)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                activePeriod === key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative z-50">
          <DateRangePicker
            dateRange={{ from: date?.from, to: date?.to }}
            onChange={(range) => setDate(range)}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card, i) => (
          <div
            key={i}
            className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">{card.title}</p>
              <div className={`rounded-lg ${card.bgColor} p-2`}>
                <card.icon className={`h-4 w-4 ${card.color}`} />
              </div>
            </div>
            <p className="mt-3 text-2xl font-bold text-gray-900">
              {formatCurrency(card.value)}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-gray-400">{card.description}</span>
              {card.change !== undefined && card.change !== 0 && (
                <span
                  className={`text-xs font-medium ${
                    card.change > 0 ? "text-emerald-600" : "text-red-500"
                  }`}
                >
                  {card.change > 0 ? "+" : ""}
                  {card.change}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {statistic && (
          <>
            <RevenueEvolutionChart data={statistic.monthlyBreakdown} />
            <RevenueDistributionChart data={statistic.percentages} />
          </>
        )}
      </div>

      {/* Tables */}
      <div className="space-y-6">
        <OnHoldInvoiceTable />
        <LateInvoiceTable />
      </div>
    </div>
  );
}
